// mapRouteProgress.js
// ---------------------------------------------
// Route progress driven by GPS + Turf.js
// ---------------------------------------------
// Exported API:
//
//   export function setupRouteProgress(options)
//
// Options:
//   map                (required) : mapboxgl.Map instance
//   routeLine          (optional) : initial LineString Feature (Turf / GeoJSON)
//   geolocateControl   (optional) : existing mapboxgl.GeolocateControl
//   maxDistanceKm      (optional) : default 2
//   updateIntervalMs   (optional) : default 5000
//
//   showRouteProgress      (optional) : () => void
//   hideRouteProgress      (optional) : () => void
//   updateRouteProgress    (optional) : (percent: number) => void
//
// Returned object:
//
//   {
//     setRouteLine(routeLine),
//     clearRouteLine(),
//     destroy(),
//     getLastCoords(),
//     isLocationDenied()
//   }
//
// NOTE: This module assumes `mapboxgl` and `turf` are available either as
// imports or as globals. If you're using a bundler, you can uncomment
// the imports:
//
// import mapboxgl from "mapbox-gl";
import length from '@turf/length';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import pointToLineDistance from '@turf/point-to-line-distance';
import { point } from '@turf/helpers';
import lineSlice from '@turf/line-slice';
import { log } from './utils.js';

export function setupRouteProgress(options) {
    if (!options || !options.map) {
      throw new Error("setupRouteProgress: 'map' is required");
    }
  
    const map = options.map;
    const maxDistanceKm =
      options.maxDistanceKm != null ? options.maxDistanceKm : 2;
    const updateIntervalMs =
      options.updateIntervalMs != null ? options.updateIntervalMs : 5000;
  
    // UI callbacks (no-ops by default)
    const showRouteProgress =
      options.showRouteProgress || (() => {});
    const hideRouteProgress =
      options.hideRouteProgress || (() => {});
    const updateRouteProgress =
      options.updateRouteProgress || (() => {});
  
    // Route is DYNAMIC:
    // - can be null initially
    // - can be updated multiple times via setRouteLine()
    let currentRouteLine = options.routeLine || null;
    let totalRouteLengthKm = currentRouteLine
      ? length(currentRouteLine, { units: "kilometers" })
      : 0;
  
    let routeIsSet = !!currentRouteLine;
  
    // Geolocate control: use caller's or create our own
    let geolocate = options.geolocateControl;
    const createdGeolocate = !geolocate;
  
    if (!geolocate) {
      geolocate = new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      });
      map.addControl(geolocate);
    }
  
    // ------------------------------
    // State
    // ------------------------------
    let lastCoords = null;      // { lat, lon } or null
    let locationDenied = false; // true if user rejected location
    let progressTimer = null;   // setInterval handle
  
    // ------------------------------
    // Helpers
    // ------------------------------
    function stopProgressLoop() {
      if (progressTimer) {
        clearInterval(progressTimer);
        progressTimer = null;
      }
    }
  
    function maybeStartProgressLoop() {
      // Only start if:
      // - we have coords
      // - location isn't denied
      // - a route is set
      // - and there isn't already a timer
      log('maybeStartProgressLoop', lastCoords, locationDenied, currentRouteLine, progressTimer);
      if (
        lastCoords &&
        !locationDenied &&
        currentRouteLine &&
        !progressTimer
      ) {
        progressTimer = setInterval(
          updateProgressFromLocation,
          updateIntervalMs
        );
        // Optional immediate update:
        updateProgressFromLocation();
      }
    }
  
    function distancePointToLineKm(point, line) {
      return pointToLineDistance(point, line, { units: "kilometers" });
    }
  
    function isNearRoute(point, line, maxKm) {
      const dist = distancePointToLineKm(point, line);
    //   log('isNearRoute', dist, maxKm);
      return dist <= maxKm;
    }
  
    function getClosestPointOnRoute(point, line) {
      return nearestPointOnLine(line, point, { units: "kilometers" });
    }
  
    function getRouteProgressPercent(snappedPoint, line) {
      if (!snappedPoint || !line || totalRouteLengthKm === 0) return 0;
  
      const startPoint = point(line.geometry.coordinates[0]);
      const subLine = lineSlice(startPoint, snappedPoint, line);
  
      const travelledKm = length(subLine, { units: "kilometers" });
      const percent = (travelledKm / totalRouteLengthKm) * 100;

    //   //Round to 4 decimal places
    //   percent = Math.round(percent * 10000) / 10000;
  
      return Math.max(0, Math.min(100, percent));
    }
  
    // ------------------------------
    // Main periodic update
    // ------------------------------
    function updateProgressFromLocation() {
      // If no route is set, or we don't have coords, or location denied,
      // don't call any UI functions.
      if (!routeIsSet || !currentRouteLine || !lastCoords || locationDenied) {
        hideRouteProgress();
        return;
      }
  
      const userPoint = point([lastCoords.lon, lastCoords.lat]);
  
      // 1) Only show progress if user is near the current route
      const near = isNearRoute(userPoint, currentRouteLine, maxDistanceKm);
      if (!near) {
        hideRouteProgress();
        return;
      }
  
      // 2) Get closest point on the route
      const snapped = getClosestPointOnRoute(userPoint, currentRouteLine);
  
      // 3) Compute % complete
      const percent = getRouteProgressPercent(snapped, currentRouteLine) || 0;
  
      // 4) Tell your UI about it
      showRouteProgress();
      updateRouteProgress(percent, snapped);
    }
  
    // ------------------------------
    // Geolocate event wiring
    // ------------------------------
    geolocate.on("geolocate", (e) => {
      lastCoords = {
        lat: e.coords.latitude,
        lon: e.coords.longitude,
      };
      locationDenied = false;
    //   log('geolocate event', lastCoords, locationDenied);
  
      // Don't start anything until a routeLine is set
      maybeStartProgressLoop();
    });

    // Disabled because also triggers when user drags the map.
    // geolocate.on('trackuserlocationend', () => {
    //     // User manually stopped tracking
    //     stopProgressLoop();
    //     hideRouteProgress();
    // });

    // geolocate.on('trackuserlocationstart', () => {
    //     // User manually enabled tracking again
    //     maybeStartProgressLoop();
    // });
      
  
    geolocate.on("error", (err) => {
      // 1 = PERMISSION_DENIED
      if (err && (err.code === 1 || err.PERMISSION_DENIED === 1)) {
        locationDenied = true;
        lastCoords = null;
        hideRouteProgress();
        stopProgressLoop();
      }
    });
  
    // ------------------------------
    // Public API
    // ------------------------------
    function setRouteLine(routeLine) {
      currentRouteLine = routeLine || null;
      routeIsSet = !!routeLine;
  
      totalRouteLengthKm = currentRouteLine
        ? length(currentRouteLine, { units: "kilometers" })
        : 0;
  
      if (!currentRouteLine) {
        // Route removed → stop updating and hide UI
        hideRouteProgress();
        stopProgressLoop();
        return 'no route';
      } else {
        // Route is set (or changed). If we already have coords, we can start/continue.
        maybeStartProgressLoop();
        // log('totalRouteLengthKm:', totalRouteLengthKm);
        return 'route set';
      }
    }
  
    function clearRouteLine() {
      setRouteLine(null);
    }
  
    function destroy() {
      hideRouteProgress();
      stopProgressLoop();
      if (createdGeolocate) {
        map.removeControl(geolocate);
      }
    }
  
    function getLastCoords() {
      return lastCoords;
    }
  
    function isLocationDenied() {
      return locationDenied;
    }
  
    return {
      setRouteLine,
      clearRouteLine,
      destroy,
      getLastCoords,
      isLocationDenied,
    };
  }
  
  /*
  // Example usage:
  import { setupRouteProgress } from "./routeProgress.js";
// If using modules for these too:
// import mapboxgl from "mapbox-gl";
// import * as turf from "@turf/turf";

const map = new mapboxgl.Map({
  container: "map",
  style: "mapbox://styles/...",
  center: [0, 0],
  zoom: 10,
});

const geolocate = new mapboxgl.GeolocateControl({
  positionOptions: { enableHighAccuracy: true },
  trackUserLocation: true,
});
map.addControl(geolocate);

// Your DOM-specific implementations:
function showRouteProgressUI() {
  document.querySelector("#route-progress").classList.remove("hidden");
}

function hideRouteProgressUI() {
  document.querySelector("#route-progress").classList.add("hidden");
}

function updateRouteProgressUI(percent, closestPoint) {
  const bar = document.querySelector("#route-progress-bar");
  if (!bar) return;
  bar.style.width = `${percent}%`;
}

// Wire everything together once
const routeProgress = setupRouteProgress({
  map,
  geolocateControl: geolocate,
  maxDistanceKm: 2,
  updateIntervalMs: 5000,
  showRouteProgress: showRouteProgressUI,
  hideRouteProgress: hideRouteProgressUI,
  updateRouteProgress: updateRouteProgressUI,
});

// Later, when your route is available:
function onRouteLoaded(routeCoords) {
  const routeLine = {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: routeCoords,
    },
  };

  routeProgress.setRouteLine(routeLine);
}

// If the route changes:
function onRouteChanged(newRouteCoords) {
  const newRouteLine = {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: newRouteCoords,
    },
  };
  routeProgress.setRouteLine(newRouteLine);
}

// If the route is removed:
function clearRoute() {
  routeProgress.clearRouteLine();
}
*/